import type { SupabaseClient, User } from "@supabase/supabase-js";
import type { Database } from "../integrations/supabase/supabaseTypes";
import type { PlayerOwner } from "../features/payment/types";

export type OAuthProvider = "google" | "github";

export type LinkAccountResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason:
        | "invalid-email"
        | "no-anonymous-session"
        | "link-failed"
        | "not-implemented";
      readonly message?: string;
    };

export interface LinkAccountService {
  linkAccountWithEmail(email: string): Promise<LinkAccountResult>;
  linkAccountWithOAuth(provider: OAuthProvider): Promise<LinkAccountResult>;
  fetchOwner(): Promise<PlayerOwner | null>;
  onAuthStateChange(listener: (owner: PlayerOwner | null) => void): () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function createLinkAccountService(
  client: SupabaseClient<Database>,
): LinkAccountService {
  return {
    async linkAccountWithEmail(email) {
      const trimmed = email.trim();
      if (!EMAIL_PATTERN.test(trimmed)) {
        return { ok: false, reason: "invalid-email" };
      }
      const sessionResult = await client.auth.getSession();
      if (sessionResult.error) {
        return {
          ok: false,
          reason: "link-failed",
          message: sessionResult.error.message,
        };
      }
      const user = sessionResult.data.session?.user ?? null;
      if (user === null || user.is_anonymous !== true) {
        return { ok: false, reason: "no-anonymous-session" };
      }
      const update = await client.auth.updateUser({ email: trimmed });
      if (update.error) {
        return {
          ok: false,
          reason: "link-failed",
          message: update.error.message,
        };
      }
      return { ok: true };
    },

    async linkAccountWithOAuth(provider) {
      const linkIdentity = (
        client.auth as unknown as {
          linkIdentity?: (input: {
            provider: OAuthProvider;
          }) => Promise<{ error: { message: string } | null }>;
        }
      ).linkIdentity;
      if (typeof linkIdentity !== "function") {
        return { ok: false, reason: "not-implemented" };
      }
      try {
        const result = await linkIdentity.call(client.auth, { provider });
        if (result.error) {
          return {
            ok: false,
            reason: "link-failed",
            message: result.error.message,
          };
        }
        return { ok: true };
      } catch (error) {
        return {
          ok: false,
          reason: "link-failed",
          message: errorMessage(error),
        };
      }
    },

    async fetchOwner() {
      const result = await client.auth.getUser();
      if (result.error || result.data.user === null) {
        return null;
      }
      return ownerFromUser(result.data.user);
    },

    onAuthStateChange(listener) {
      const subscription = client.auth.onAuthStateChange((_event, session) => {
        const user = session?.user ?? null;
        listener(user === null ? null : ownerFromUser(user));
      });
      return () => {
        subscription.data.subscription.unsubscribe();
      };
    },
  };
}

function ownerFromUser(user: User): PlayerOwner {
  return {
    ownerId: user.id,
    ownerKind: user.is_anonymous === true ? "anonymous" : "signedIn",
  };
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
